<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to cut off list" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="faceai" pageId="activation-self-service" color="white"></help-link>
                    <v-chip v-show="!loading" label :class="status.color" dark>
                        <span >{{ status.status }}</span>
                    </v-chip>
                </v-toolbar>
            </v-card>
            <v-alert :value="true" color="info" icon="info" outline class="mt-8">
                To enable your clock-in/clock-out with Facial Recognition, upload your two recent, different 2x2 photos (ideally 1680x1680 pixels and around 500KB each) with your face fully visible—no eyeglasses, hats, hair, or other coverings.  
            </v-alert>
            <v-layout row wrap> 
                <v-flex xs12>
                    <v-card class="elevation-1 transparent">
                        <v-toolbar flat height="40">
                            <span class="font-weight-bold">Enrolled Photos</span>
                            <v-spacer></v-spacer>
                            <v-btn v-if="hasAddRights&&!isMaxPhotoCount&&!loading" small class="primary" title="Add photo" @click="showAddPhoto">
                                <v-icon class="mr-5">add</v-icon>Add Photo
                            </v-btn>
                        </v-toolbar>
                        <v-data-table
                            :headers="isMobile?header_mobile:header"
                            :items="user_descriptors"
                            :loading="loading"  
                            hide-actions
                        >
                            <template v-slot:items="props">
                                <tr>
                                    <td>
                                        <v-img :src="commonfn.getFaceAIPhoto(props.item.photoFileName)" width="30"></v-img>
                                    </td>
                                    <td>{{ props.item.createdOn | moment('MMM DD, YYYY') }}</td>
                                    <td>Verified</td>
                                    <td v-if="!isMobile">
                                        <v-chip style="margin:-5px 0px 0px -5px;">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.createdBy.userId)"></v-img>
                                            </v-avatar>
                                            {{props.item.createdBy.firstName}} {{props.item.createdBy.lastName}} 
                                        </v-chip>
                                    </td>
                                    <td>
                                        <v-btn v-if="hasDeleteRights" icon small title="Delete" @click="showDelete(props.item)">
                                            <v-icon small>delete</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-flex>
            </v-layout>
        </v-flex>

        <v-dialog v-model="dialogUploadPhoto" persistent fullscreen hide-overlay transition="dialog-bottom-transition">   
            <upload-photo :pageKey="pageKeys.attendance_face_activation" @send="processPhoto" :dialogUploadPhoto.sync="dialogUploadPhoto"></upload-photo>
        </v-dialog>
        
        <v-dialog v-model="dialogModelTraining" width="600" persistent>
            <v-card class="elevation-4 rounded-4"> 
                <!-- SUCCESS HEADER -->
                <v-toolbar v-if="!processingError" flat height="55">
                    <v-toolbar-title class="subheading font-weight-medium">Setting up Face AI</v-toolbar-title>
                </v-toolbar>

                <!-- ERROR HEADER -->
                <v-toolbar v-else flat height="55">
                    <v-icon color="error">error_outline</v-icon>
                    <v-toolbar-title class="subheading font-weight-medium error--text">Unable to Register Photo</v-toolbar-title>
                </v-toolbar>

                <v-card-text class="p-30 text-xs-center">
                    <!-- PROCESSING TRACK -->
                    <div v-if="!processingError">
                        <v-progress-circular 
                            :value="processingValue" 
                            :size="70" 
                            :width="6" 
                            color="primary" 
                            class="mb-4"
                        >
                            <span class="caption font-weight-bold">{{ processingValue }}%</span>
                        </v-progress-circular>
                        
                        <h3 class="title font-weight-regular mb-2 primary--text">{{ processingText }}</h3>
                        <p class="body-1 grey--text text--darken-1 mx-3">
                            Please do not close, refresh, or navigate away from this page while we configure your biometric setup.
                        </p>
                    </div>

                    <!-- ERROR TRACK -->
                    <div v-else>
                        <v-alert :value="true" color="error" outline class="text-xs-left body-1">
                            {{ processingMessage }}
                        </v-alert>
                        
                        <p class="body-2 grey--text text--darken-2 mt-10">
                            Please review the 2x2 requirements and try capturing or uploading your photo again.
                        </p>
                    </div>
                </v-card-text>

                <v-divider></v-divider>

                <!-- ACTION BUTTONS -->
                <v-card-actions v-if="processingError">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="dialogModelTraining = false" class="mr-20">
                        Go Back and Retry
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogDelete" width="500">
            <delete ref="refDeleteComponent" :dialogDelete.sync="dialogDelete" @okDelete="deletePhoto"></delete>
        </v-dialog>

    </v-layout> 
</template>

<script>

import * as faceapi from 'face-api.js'
import commonfn from '@/common/functions'   //for refactor
import faceAIApi from '@/api/faceai'
import pageKeys from '@/common/pagekeys'
import awsS3Helper from '@/helpers/aws-s3'
import { hasAddRights, hasDeleteRights } from '@/helpers/access-permissions'

export default {     
    name: 'FaceRecognitionActivition',
    metaInfo: {
        title: 'Face Recognition Activition',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Face Recognition Activition' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'upload-photo': () => import('@/components/FaceRecognition/UploadPhoto'),
        'delete': () => import('@/components/BasicPopups/Delete')
    },
    data () {
        return {
            pageKeys,
            commonfn,
            awsS3Helper,
            error: false,
            errorMessage: '',
            loading: false,
            dialogUploadPhoto: false,
            dialogModelTraining: false,
            dialogDelete: false,
            processingValue: 0,
            header: [
                {text: 'Photo',align: 'left',value: '', width:'20%', sortable:false},
                {text: 'Uploaded On',align: 'left',value: '', width:'20%',sortable:false},
                {text: 'Status',align: 'left',value: '', width:'20%',sortable:false},
                {text: 'Created By',align: 'left',value: '', width:'20%',sortable:false},
                {text: 'Delete',align: 'left',value: '', width:'10%',sortable:false}
            ],
            header_mobile: [
                {text: 'Photo',align: 'left',value: '', width:'20%', sortable:false},
                {text: 'Uploaded On',align: 'left',value: '', width:'20%',sortable:false},
                {text: 'Status',align: 'left',value: '', width:'20%',sortable:false},
                {text: 'Delete',align: 'left',value: '', width:'10%',sortable:false}
            ],
            base64Image: null,
            photoFileName: null,
            descriptor: null,
            selectedUserFaceDescriptorId: null,
            user_descriptors: [],
            processingText: 'Starting...',
            processingMessage: null,
            processingError: false
        }
    },
    methods: {
        /**
         * Photo upload and delete methods
         */
        async deletePhoto() {
            try{
                const response = await faceAIApi.deleteUserFaceDescriptor(this.selectedUserFaceDescriptorId)
                if (response && response.status == 200) {
                    await this.getUserDescriptors()
                    this.dialogDelete = false
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        showDelete(data) {
            this.$refs.refDeleteComponent.setMessage("Delete selected photo?")
            this.selectedUserFaceDescriptorId = data.userFaceDescriptorId
            this.dialogDelete = true
        },
        showAddPhoto() {
            this.dialogUploadPhoto = true
        },

        /**
         * Photo validation methods
         */
        // Validates the overall resolution of the image asset.
        validateImageResolution(img) {
            const MIN_WIDTH = 600;
            const MIN_HEIGHT = 600;

            if (img.width < MIN_WIDTH || img.height < MIN_HEIGHT) {
                return {
                    isValid: false,
                    error: `The uploaded image resolution is too low (${img.width}x${img.height}px). Please upload a clear photo that is at least ${MIN_WIDTH}x${MIN_HEIGHT} pixels.`
                };
            }
            return { isValid: true };
        },

        // Validates that the face fills a proper ID layout (50%-80%) and safeguards against squished or stretched uploads.
        validateFaceDimensions(detection, img) {
            // Anti-distortion logic: verify the image aspect ratio is normal
            // Standard ratio is 1.0 (Square) up to ~1.4 (Standard 4:3 / 3:2 portrait photos)
            const aspectRatio = img.height / img.width;
            if (aspectRatio > 1.6 || aspectRatio < 0.6) {
                return {
                    isValid: false,
                    error: "The uploaded image appears distorted or heavily stretched. Please upload a proportional 2x2 or portrait photo."
                };
            }

            const faceWidth = detection.box.width;
            const faceHeight = detection.box.height;

            // Use the smaller image dimension as the baseline ruler.
            // This treats both square crops and portrait crops fairly.
            const frameBaseline = Math.min(img.width, img.height);
            const faceBaseline = Math.min(faceWidth, faceHeight);

            // Tighten face target zone to standard ID metrics (Minimum 50%)
            const MIN_FACE_RATIO = 0.50;
            const faceRatio = faceBaseline / frameBaseline;

            if (faceRatio < MIN_FACE_RATIO) {
                return {
                    isValid: false,
                    error: `Your face occupies only ${Math.round(faceRatio * 100)}% of the photo space. Please zoom/crop closer so your face takes up at least 50% to 80% of the image frame.`
                };
            }

            // Blurry crop safeguard: ensure the face bounding box contains real pixel resolution
            const MIN_FACE_PIXELS = 300; 
            if (faceWidth < MIN_FACE_PIXELS || faceHeight < MIN_FACE_PIXELS) {
                return {
                    isValid: false,
                    error: "The face details in this cropped photo are too pixelated or blurry. Please upload a higher-quality photo."
                };
            }

            return { isValid: true };
        },

        // There is already a resuable methods for face ai activation in helpers/face-ai-activation-helper.js but not implemented.
        // Requires further testing once implemeted in both Self Service Activation and in Settings > Face AI activation

        /**
         * Photo processing and model training methods
         */
        async processPhoto(data) {
            this.dialogModelTraining = true
            this.processingError = false
            this.processingMessage = ""
            this.error = false 
            this.errorMessage = ''

            this.base64Image = data.base64Image
            this.photoFileName = data.fileName

            // Step 1: Initialize & Load Models
            this.processingText = 'Initializing secure Face AI engine...'
            this.processingValue = 20
            await this.loadModels()

            // Step 2: Validate Framing and Metrics
            this.processingText = 'Analyzing photo framing and clarity...'
            this.processingValue = 50
            const success = await this.trainModel()

            // Discontinue step chain if validations fail
            if (!success) {
                await this.clearMemory()
                return
            }

            // Step 3: Securely Database Enrolled Vector
            this.processingText = 'Encrypting and securing face descriptors...'
            this.processingValue = 80
            await this.saveDescriptor()

            // Step 4: Tear down local memory instances
            this.processingText = 'Finalizing configuration setup...'
            this.processingValue = 100
            await this.clearMemory()
            
            // Re-render UI list structure
            await this.getUserDescriptors()

            this.dialogModelTraining = false
        },
        async loadModels() {
            await faceapi.nets.tinyFaceDetector.loadFromUri('/face-ai/models')
            await faceapi.nets.faceLandmark68Net.loadFromUri('/face-ai/models')
            await faceapi.nets.faceRecognitionNet.loadFromUri('/face-ai/models')
        },
        async trainModel() {
            const label = `${this.$store.state.firstName}_${this.$store.state.lastName}`
            const img = new Image()
            img.src = this.base64Image

            await new Promise((resolve) => (img.onload = resolve))

            // 1. Run overall image quality check
            const resolutionCheck = this.validateImageResolution(img);
            if (!resolutionCheck.isValid) {
                this.processingError = true;
                this.processingMessage = resolutionCheck.error;
                return false
            }

            // Run AI detection
            const detections = await faceapi
                .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions())
                .withFaceLandmarks()
                .withFaceDescriptor()

            // 2. Run Face Specific Checks
            if (detections) {
                const faceCheck = this.validateFaceDimensions(detections.detection, img);
                if (!faceCheck.isValid) {
                    this.processingError = true
                    this.processingMessage = faceCheck.error
                    return false
                }

                // Everything passed, save the descriptor
                this.descriptor = new faceapi.LabeledFaceDescriptors(label, [detections.descriptor])
                return true
            }else{
                this.processingError = true
                this.processingMessage = "No face detected, or photo quality is too low. Please upload a clear 2x2 photo."
                return false;
            }
        },
        async clearMemory() {
            faceapi.nets.tinyFaceDetector.dispose()
            faceapi.nets.faceLandmark68Net.dispose()
            faceapi.nets.faceRecognitionNet.dispose()

            this.base64Image = null
            this.photoFileName = null
            this.descriptor = null
        },

        /**
         * Save photo descriptors
         */
        async saveDescriptor() {
            try{
                const data = {
                    photoFileName: this.photoFileName,
                    descriptors: JSON.stringify(this.descriptor.descriptors)
                }
                await faceAIApi.saveFaceDescriptors(data)
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },

        /**
         * Pull user descriptors from API and display in grid
         */
        async getUserDescriptors() {
            try{
                this.loading = true
                const response = await faceAIApi.getUserFaceDescriptors()
                if (response && response.status == 200) {
                    this.user_descriptors = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        }
    },
    async created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: 'Face Recognition Activation',
            headerPageTitle: ''
        })

        this.getUserDescriptors()
    },
    computed: {
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
        hasDeleteRights() {
            return hasDeleteRights(this.$router.currentRoute.path)
        },
        isMaxPhotoCount() {
            return this.user_descriptors.length == 2
        },
        status() {
            if (this.user_descriptors.length) {
                if (this.user_descriptors.length == 2) {
                    return {
                        status: 'Active',
                        color: 'green'
                    }
                } else {
                    return {
                        status: 'In Complete',
                        color: 'orange'
                    }
                }
            }else{
                return {
                        status: 'Inactive',
                        color: 'orange'
                    }
            }
        },
    }
}
</script>